import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ProgressBar } from 'primereact/progressbar';
import { apiAgent } from 'config/axiosConfig';
import MainLayout from 'layouts/MainLayout';
import TokenIcon from '@mui/icons-material/Token';
import BuildIcon from '@mui/icons-material/Build';
import { connect } from "react-redux";
import { Dot } from 'lucide-react';
import UpgradeDialog from 'components/TestCaseGeneratorBot/UpgradeDialog';

const TransactionHistory = ({ projectId, project }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const ITEMS_PER_PAGE = 100;
  const [totalBuilds, setTotalBuilds] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [remainingBuilds, setRemainingBuilds] = useState(0);
  const [remainingTokens, setRemainingTokens] = useState(0);
  const [planRenewalDate, setPlanRenewalDate] = useState('');
  const [upgradeDialogVisible, setUpgradeDialogVisible] = useState(false);

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!projectId) return;

      setIsLoading(true);
      try {
        const response = await apiAgent({
          method: 'get',
          url: '/api/v1/ai_transactions/',
          params: {
            project_id: projectId,
            page: page,
            page_size: ITEMS_PER_PAGE
          }
        });
        setTransactions(response.data?.ai_transactions || []);
        // Assuming the API returns total_pages in the response
        setTotalPages(response.data?.total_pages || 1);
        // New Changes 
        setTotalBuilds(response.data?.total_builds);
        setRemainingBuilds(response.data?.remaining_builds);
        setTotalTokens(response.data?.total_tokens);
        setRemainingTokens(response.data?.remaining_tokens);
        setPlanRenewalDate(response.data?.plan_renewal_date);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [projectId, page]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const headerStyle = {
    fontWeight: 700,
    fontSize: '13px',
    backgroundColor: 'var(--background-primary)',
    padding: '10px',
    border: '1px solid var(--custom-tbl-border-color)',
  };

  const cellStyle = {
    fontSize: '12px',
    padding: '6px',
    border: '1px solid var(--custom-tbl-border-color)',
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatNumber = (num) => {
    if (num >= 1_000_000_000_000) {
      return { value: +(num / 1_000_000_000_000).toFixed(1), unit: 'T' }; // Trillion
    }
    if (num >= 1_000_000_000) {
      return { value: +(num / 1_000_000_000).toFixed(1), unit: 'B' }; // Billion
    }
    if (num >= 1_000_000) {
      return { value: +(num / 1_000_000).toFixed(1), unit: 'M' }; // Million
    }
    if (num >= 1_000) {
      return { value: +(num / 1_000).toFixed(1), unit: 'K' }; // Thousand
    }
    return { value: num, unit: '' };
  };

  const getUsedPercentage = (total, remaining) => {
    if (total === 0) return 0;
    return ((total - remaining) / total) * 100;
  }

  const getProgressBarColor = (percentage) => {
    return percentage >= 75 ? 'red' : undefined;
  };

  const getTextColor = (percentage) => {
    return percentage >= 75 ? 'text-red-600' : 'text-[var(--text-primary)]';
  };

  const formatPercentage = (percentage) => {
    return percentage % 1 === 0 ? percentage : percentage.toFixed(1);
  };

  const renderTransactionTable = () => (
    <TableContainer sx={{ maxHeight: "365px" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell style={headerStyle}>Date</TableCell>
            <TableCell style={headerStyle}>Tokens Used</TableCell>
            <TableCell style={headerStyle}>Tokens Added</TableCell>
            <TableCell style={headerStyle}>User</TableCell>
            <TableCell style={headerStyle}>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id} hover>
              <TableCell style={cellStyle}>
                {formatDate(transaction.created_at)}
              </TableCell>
              <TableCell style={cellStyle}> {transaction.tokens_used && (
                <span className='text-red-600'>
                  - {transaction.tokens_used}
                </span>
              )}
              </TableCell>
              <TableCell style={cellStyle}> {transaction.tokens_added && (
                <span className='text-green-600'>
                  + {`${formatNumber(transaction.tokens_added).value}${formatNumber(transaction.tokens_added).unit}`}
                </span>
              )}
              </TableCell>
              <TableCell style={cellStyle}>{transaction.user?.name}</TableCell>
              <TableCell style={cellStyle}>{transaction.message}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <MainLayout
        headerText="Token History"
        subtitleText="View all your AI Token transactions"
        infoText="This shows the history of AI Tokens used across different tasks"
        isLoading={isLoading}
        totalPages={totalPages}
        pageNumber={page}
        onPageChange={handlePageChange}
        contentStyle={{ p: 0, paddingTop: "0px" }}
      >
        <Box sx={{ maxHeight: "calc(100vh - 160px)", overflow: "auto", padding: "24px" }}>
          {transactions.length === 0 && !isLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <span style={{ color: "var(--text-secondary)", fontSize: '15px' }}>No transactions found</span>
            </Box>
          ) : (
            <>
              <div className='w-full mb-4'>
                <div className='flex items-center mb-2'>
                  <div className='pr-2'>
                    <span className='font-medium'>Plan Renewal On: </span>
                    {planRenewalDate}
                    {project?.custom_openai_api_key_enabled && project?.openai_api_key && (
                      <span className='capitalize ml-2'>(We are not recording transaction for Custom API Key)</span>
                    )}
                  </div>
                  <Dot size={14} color='var(--text-primary)' />
                  <button
                    className='flex items-center border-none text-capitalize !font-medium !rounded-md bg-transparent'
                    onClick={() => setUpgradeDialogVisible(true)}
                  >
                    <span className='text-blue-500'>Upgrade</span>
                  </button>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='p-3 bg-[var(--background-primary)]' style={{ border: "1px solid var(--borderColor)" }}>
                    <div className='flex items-center gap-2 mb-4'>
                      <div className='flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 p-2'>
                        <TokenIcon className='text-blue-600' style={{ fontSize: '1.35rem' }} />
                      </div>
                      <span className='!text-xl font-medium'>Token Usage</span>
                    </div>
                    <div className='mb-2'>
                      <div className='flex justify-between mb-1'>
                        <span className={`text-sm ${getTextColor(getUsedPercentage(totalTokens, remainingTokens))}`}>
                          Used: {totalTokens - remainingTokens} / {totalTokens}
                        </span>
                        <span className={`text-sm ${getTextColor(getUsedPercentage(totalTokens, remainingTokens))}`}>
                          {formatPercentage(getUsedPercentage(totalTokens, remainingTokens))}% Used
                        </span>
                      </div>
                      <ProgressBar
                        value={getUsedPercentage(totalTokens, remainingTokens)}
                        showValue={false}
                        className='h-2'
                        color={getProgressBarColor(getUsedPercentage(totalTokens, remainingTokens))}
                      />
                    </div>
                  </div>
                  <div className='p-3 bgPrimary border !border-[var(--borderColor)]'>
                    <div className='flex items-center gap-2 mb-4'>
                      <div className='flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 p-2'>
                        <BuildIcon className='text-blue-600' style={{ fontSize: '1.35rem' }} />
                      </div>
                      <span className='!text-xl font-medium'>Build Usage</span>
                    </div>
                    <div className='mb-2'>
                      <div className='flex justify-between mb-1'>
                        <span className={`text-sm ${getTextColor(getUsedPercentage(totalBuilds, remainingBuilds))}`}>
                          Used: {totalBuilds - remainingBuilds} / {totalBuilds}
                        </span>
                        <span className={`text-sm ${getTextColor(getUsedPercentage(totalBuilds, remainingBuilds))}`}>
                          {formatPercentage(getUsedPercentage(totalBuilds, remainingBuilds))}% Used
                        </span>
                      </div>
                      <ProgressBar
                        value={getUsedPercentage(totalBuilds, remainingBuilds)}
                        showValue={false}
                        className='h-2'
                        color={getProgressBarColor(getUsedPercentage(totalBuilds, remainingBuilds))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {renderTransactionTable()}
            </>
          )}
        </Box>
      </MainLayout>
      <UpgradeDialog
        visible={upgradeDialogVisible}
        onClose={() => setUpgradeDialogVisible(false)}
        availableTokens={remainingTokens}
      />
    </>

  );
};


function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    project: state.common.selectedProject,
  };
}

export default connect(mapStateToProps)(TransactionHistory);